/**
 * Protected Route Component
 * 
 * This component provides route protection based on authentication status
 * and user permissions. It redirects unauthenticated users to login
 * and shows appropriate messages for insufficient permissions.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import './auth.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredPermissions?: string[];
  fallbackComponent?: React.ReactNode;
  redirectTo?: string;
  showPermissionDenied?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAuth = true,
  requiredPermissions = [],
  fallbackComponent = null,
  redirectTo = '/auth',
  showPermissionDenied = true 
}) => {
  const { isAuthenticated, user, permissions, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading Productive Capabilities Data Lab... Please wait while we verify your access</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to login if authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check permissions if required
  if (requiredPermissions.length > 0 && isAuthenticated) {
    const hasRequiredPermissions = requiredPermissions.every(permission => {
      if (!permissions || typeof permission !== 'string') return false;
      
      // Special case for admin role - check user.role instead of permissions
      if (permission === 'admin') {
        return user?.role === 'admin';
      }
      
      // Check all permission arrays for other permissions
      const allPermissions = [
        ...permissions.valueChains,
        ...permissions.countries,
        ...permissions.dataPermissions
      ];
      
      return allPermissions.some(perm => 
        perm.permission_level === permission ||
        (permission.includes('_') && perm.permission_level === permission.split('_')[0])
      );
    });

    if (!hasRequiredPermissions) {
      if (fallbackComponent) {
        return <>{fallbackComponent}</>;
      }

      if (showPermissionDenied) {
        return (
          <div className="auth-page">
            <div className="auth-container">
              <div className="auth-card">
                <div className="permission-denied">
                  <h2>Access Denied</h2>
                  <p>
                    You don't have the required permissions to access this page.
                  </p>
                  <div className="permission-details">
                    <h3>Required Permissions:</h3>
                    <ul>
                      {requiredPermissions.map((permission, index) => (
                        <li key={index}>{permission}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="permission-actions">
                    <button
                      className="auth-button primary"
                      onClick={() => window.history.back()}
                    >
                      Go Back
                    </button>
                    <button
                      className="auth-button secondary"
                      onClick={() => window.location.href = '/dashboard'}
                    >
                      Go to Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      return <Navigate to="/dashboard" replace />;
    }
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default ProtectedRoute;
